import { Card, CbhIcon, Text, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack } from "@mui/material";
import { useGetCredentialSharingConfiguration } from "@src/appV2/Facilities/api/useGetCredentialSharingConfiguration";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { ChartingCredentialsSetUpContainer } from "@src/appV2/redesign/Charting/ChartingCredentialsSetUpContainer";
import { SHIFT_CREDENTIALS_CONFIRMATION_KEY } from "@src/appV2/redesign/Charting/constants";
import { YourChartingLoginBottomSheetContainer } from "@src/appV2/redesign/Charting/YourChartingLoginBottomSheetContainer";
import { Button } from "@src/appV2/redesign/components/Button";
import { useGetChartingCredentials } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

interface EnsureChartingIsCompleteCardProps {
  shiftId: string;
  workplaceId: string;
}

type ShiftCredentialsConfirmation = Record<string, boolean>;

export function EnsureChartingIsCompleteCard(props: EnsureChartingIsCompleteCardProps) {
  const { shiftId, workplaceId } = props;

  const worker = useDefinedWorker();
  const chartingCredentialsModalState = useModalState();
  const yourChartingLoginBottomSheetModalState = useModalState();
  const [confirmedShifts] = useLocalStorage<ShiftCredentialsConfirmation>(
    SHIFT_CREDENTIALS_CONFIRMATION_KEY,
    {}
  );

  const { credentialSharingConfiguration } = useGetCredentialSharingConfiguration(workplaceId, {
    enabled: isDefined(workplaceId),
  });

  const { chartingReminders } = credentialSharingConfiguration ?? {};

  const { data: chartingCredentialsResponse, isLoading: isLoadingCredentials } =
    useGetChartingCredentials(worker.userId, workplaceId);

  const chartingCredentials =
    chartingCredentialsResponse?.data?.[0]?.attributes?.chartingCredentials;

  const hasLoggedIn = confirmedShifts[shiftId] ?? false;

  const handleOpenChartingCredentials = () => {
    if (hasLoggedIn) {
      yourChartingLoginBottomSheetModalState.openModal();
    } else {
      chartingCredentialsModalState.openModal();
    }
  };

  const getChartingRequiredMessage = () => {
    if (chartingReminders?.clockOut.enabled) {
      return "Your shift access will be limited if charting is incomplete. Please finish all charting tasks before you leave!";
    }

    return "Charting is required for this shift. Make sure all charting tasks are marked complete before you leave!";
  };

  if (!chartingReminders?.inProgressShiftDetails.enabled) {
    return null;
  }

  return (
    <>
      <Card>
        <CardContent sx={{ padding: 7 }}>
          <Stack direction="row" spacing={4} alignItems="flex-start">
            <CbhIcon type="software" size="large" />
            <Stack spacing={3} sx={{ flex: 1 }}>
              <Text bold variant="body1">
                Ensure charting is complete
              </Text>
              <Text variant="body2" sx={{ color: (theme) => theme.palette.text.secondary }}>
                {getChartingRequiredMessage()}
              </Text>
            </Stack>
          </Stack>

          <Button
            fullWidth
            sx={{ marginTop: 5 }}
            size="small"
            onClick={handleOpenChartingCredentials}
          >
            My charting login
          </Button>
        </CardContent>
      </Card>

      <ChartingCredentialsSetUpContainer
        shiftId={shiftId}
        workplaceId={workplaceId}
        modalState={chartingCredentialsModalState}
        onComplete={() => {
          chartingCredentialsModalState.closeModal();
        }}
      />

      <YourChartingLoginBottomSheetContainer
        modalState={yourChartingLoginBottomSheetModalState}
        shiftId={shiftId}
        workplaceId={workplaceId}
        workerId={worker.userId}
        chartingCredentials={chartingCredentials}
        isLoadingCredentials={isLoadingCredentials}
      />
    </>
  );
}
